import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import Navbar from "../staticView/globalComponents/Navbar";
import Footer from "../staticView/globalComponents/Footer";
import TopBar from "../staticView/globalComponents/TopBar";
import { usePortalReviewAccess } from "../../hooks/usePortalReviewAccess";
import { useHrAccess } from "../../hooks/useHrAccess";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setEmployeeOnline } from "../../store/allocation/allocationAuthSlice";
import api from "../../utils/api";

function Layout() {
  const location = useLocation();
  const { canSeeReviewButton } = usePortalReviewAccess();
  const { isHrAdmin, isSuperAdmin } = useHrAccess();
  const dispatch = useAppDispatch();
  const employee = useAppSelector((state) => state.allocationAuth.employee);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState("");
  const canSelfMark =
    Boolean(employee?.can_self_mark) ||
    Boolean(employee?.is_remote) ||
    Boolean(employee?.is_hybrid) ||
    isSuperAdmin;

  const markOnline = async (online: boolean) => {
    setMarking(true);
    setMarkError("");
    try {
      await api.post("/allocation-tool/employee/mark-online", {
        action: online ? "online" : "offline",
      });
      dispatch(setEmployeeOnline(online));
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Could not update online status";
      setMarkError(message);
    } finally {
      setMarking(false);
    }
  };

  const shouldHideNavbar = location.pathname.startsWith("/dashboard/employee");

  const peopleLinks = [
    { to: "/dashboard/employee/directory", label: "Directory" },
    { to: "/dashboard/employee/organization", label: "Organization" },
    { to: "/dashboard/employee/shifts", label: "Shifts" },
  ];

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2 rounded-lg text-sm ${
      isActive
        ? "bg-[#345952] text-white"
        : "bg-[#345952]/10 text-[#345952] hover:bg-[#345952]/20"
    }`;

  return (
    <div className="w-full overflow-x-hidden">
      <div className="min-h-screen w-full flex flex-col items-center overflow-x-hidden">
        <div className="sticky top-0 z-50 w-full">
          {shouldHideNavbar ? <TopBar /> : <Navbar handleGoogleLogin={() => {}} rel={null} />}
        </div>

        <div className="w-full flex-1 flex flex-col min-h-0" style={{ backgroundColor: "#FDFBF2" }}>
          <div className="px-[4%] py-2.5 flex flex-wrap gap-2 items-center border-b border-[#E8E8E4] bg-white/80 backdrop-blur-sm">
            <span className="text-sm font-semibold text-gray-600 mr-2">
              Employee Dashboard
            </span>
            {employee && canSelfMark ? (
              <button
                type="button"
                disabled={marking}
                onClick={() => markOnline(!employee.active)}
                className={`px-4 py-2 rounded-lg text-sm text-white disabled:opacity-60 ${
                  employee.active ? "bg-red-500" : "bg-green-600"
                }`}
                title={
                  employee.is_hybrid
                    ? "Hybrid: mark present here or punch at the office"
                    : isSuperAdmin
                      ? "Super Admin can mark present from the portal"
                      : "Mark present for today"
                }
              >
                {marking
                  ? "Saving..."
                  : employee.active
                    ? "Mark offline"
                    : "Mark present"}
              </button>
            ) : null}
            {markError ? (
              <span className="text-xs text-red-600">{markError}</span>
            ) : null}
            <NavLink to="/dashboard/employee/overview" className={linkClass}>
              Overview
            </NavLink>
            <NavLink to="/dashboard/employee/cases" className={linkClass}>
              Cases
            </NavLink>
            <NavLink to="/dashboard/employee/my-attendance" className={linkClass}>
              Attendance
            </NavLink>
            <NavLink to="/dashboard/employee/leaves" className={linkClass}>
              Leaves
            </NavLink>
            {canSeeReviewButton ? (
              <NavLink to="/dashboard/employee/reviews" className={linkClass}>
                Feedback
              </NavLink>
            ) : null}
            {isHrAdmin
              ? peopleLinks.map((link) => (
                  <NavLink key={link.to} to={link.to} className={linkClass}>
                    {link.label}
                  </NavLink>
                ))
              : null}
            {isSuperAdmin ? (
              <NavLink to="/dashboard/employee/leave-balance-logs" className={linkClass}>
                Balance logs
              </NavLink>
            ) : null}
          </div>

          <main className="w-full min-w-0 flex-1 flex flex-col overflow-x-hidden px-4 py-4 md:px-6 md:py-6">
            <Outlet />
          </main>
        </div>
      </div>
      {!shouldHideNavbar && <Footer />}
    </div>
  );
}

export default Layout;
